import * as React from 'react';
import {
  ApproveDeleteContactsModal,
  DeleteContactSummaryModal,
} from '@wix/contacts-ui';
import { contactsApi } from './apis/Contacts.api';
import {
  navigateTo,
  PageComponentId,
  showToast,
  ToastType,
} from '@wix/business-manager-api';
import { useBiLogger } from '../../helpers/providers/BiLoggerProvider';
import { withTranslation } from '../../util';
import { InjectedTranslateProps } from 'react-i18next';
import { AdvancedFilterDTO } from '../../domain/AdvancedFilter';

export interface DeleteContactOnFinishResponse {
  numberOfDeleted: number;
  numberOfFailed: number;
}
export interface DeleteContactContainerProps {
  metaSiteId: string;
  contactIds: string[];
  excludedContactIds?: string[];
  contactAdvancedFilter?: AdvancedFilterDTO;
  contactsCount?: number;
  origin: string;
  onFinish?(res?: DeleteContactOnFinishResponse): void;
  onCancel?(): void;
}

export enum DeleteContactSteps {
  APPROVAL = 'APROVAL',
  SUMMARY = 'SUMMARY',
}

const DeleteContactContainerComponent = (
  props: DeleteContactContainerProps & InjectedTranslateProps,
) => {
  const {
    contactIds,
    excludedContactIds,
    contactAdvancedFilter,
    onCancel,
    onFinish,
    origin,
    metaSiteId,
    contactsCount,
    t,
  } = props;
  const contactsListCount = contactsCount || contactIds.length;

  const [isLoading, setIsLoading] = React.useState(false);
  const [currentStep, setCurrentStep] = React.useState(
    DeleteContactSteps.APPROVAL,
  );
  const [numberOfDeleted, setNumberOfDeleted] = React.useState(0);
  const [numberOfFailed, setNumberOfFailed] = React.useState(0);
  const biLogger = useBiLogger();

  const sendDeleteCotactLog = () => {
    return biLogger.contactsDeleteContact({
      count_of_deleted: contactsListCount,
      origin,
      msid: metaSiteId,
    });
  };

  const handleDeleteContactsError = (err: any): void => {
    setNumberOfFailed(err.numberOfFailed);
    setIsLoading(false);
    setCurrentStep(DeleteContactSteps.SUMMARY);
    return;
  };

  const deleteContacts = async () => {
    setIsLoading(true);

    try {
      const [, response] = await Promise.all([
        sendDeleteCotactLog(),
        contactsListCount > 1
          ? contactsApi.bulkDeleteContacts(
              contactIds,
              excludedContactIds,
              contactAdvancedFilter,
            )
          : contactsApi.deleteContact(contactIds[0]),
      ]);
      const ToastMessage = t(`delete.contact.toast.success`);
      const toastConfig = {
        message: ToastMessage,
        biName: 'contacts.view-contact-banner',
        type: ToastType.SUCCESS,
      };
      showToast(toastConfig);
      return onFinish({
        numberOfFailed: 0,
        numberOfDeleted: (response as any)?.deletedContacts ?? 1,
      });
    } catch (error) {
      return handleDeleteContactsError({
        numberOfFailed: contactsListCount,
        numberOfDeleted: 0,
      });
    }
  };

  const NavigateToMembersList = () => {
    onFinish();
    navigateTo({
      pageComponentId: PageComponentId.MemberPermissions,
      contextData: {
        appState: 'members',
        referrer: 'contacts',
      },
    });
  };

  return (
    <React.Fragment>
      <ApproveDeleteContactsModal
        isOpen={currentStep === DeleteContactSteps.APPROVAL}
        numberOfContacts={contactsListCount}
        onApprove={() => deleteContacts()}
        onCancel={() => onCancel()}
        isLoading={isLoading}
      />
      <DeleteContactSummaryModal
        isOpen={currentStep === DeleteContactSteps.SUMMARY}
        onClose={() =>
          onFinish({
            numberOfFailed,
            numberOfDeleted,
          })
        }
        onNavigateToMembersList={() => NavigateToMembersList()}
        numberOfFailed={numberOfFailed}
        numberOfDeleted={numberOfDeleted}
      />
    </React.Fragment>
  );
};

export const DeleteContactContainer = withTranslation(
  DeleteContactContainerComponent,
);
